import React, { useRef, useCallback, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { fetchJson } from '../../../helpers/fetch.js';
import useRowId from './useRowId.js';
import AutoGroupValueRenderer from '../AutoGroupValueRenderer.jsx';
import { VerticalCardWithIcon } from '../../common/Cards.jsx';
import { formatNumber } from '../../common/FormattedNumber.jsx';
import { getServerSideSelectedNodes } from '../../common/AgGrid/index.jsx';
import _ from 'lodash';

import {
  datagrid_update_selection_array_path,
  datagrid_update_rows_path,
  datagrid_update_row_path,
  datagrid_add_row_path
} from '~/routes';

// Transactions contain whole row. Having multiple updates on same row, only the last one counts
export const reduceDuplicateTransactions = (transactions) => {
  for(let i=transactions.length - 1; i >= 0; --i) {
    let current_update_tx = transactions[i]['update'];
    if (!current_update_tx) continue;

    // Remove all previous updates
    for(let j=0; j < i; ++j) {
      const update_tx = transactions[j]['update']
      if (!update_tx) continue;
      if (update_tx[0].id != current_update_tx[0].id) continue;

      // Remove & adjust indexes to match
      transactions.splice(j, 1);
      --j; --i;
    }
  }

  // Remove duplicate transactions and keep last update in add
  for (let i = 0; i < transactions.length; i++) {
    const add_tx = transactions[i]['add'];
    if (!add_tx) continue;

    add_tx.forEach((a, ai) => {
      // look for a matching update
      for (let j = 0; j < transactions.length; j++) {
        const update_tx = transactions[j]['update'];
        if (!update_tx) continue;

        const idx = update_tx.map(u => u.id).lastIndexOf(a.id);
        if (idx > -1) {
          // Replace with last update
          add_tx.splice(ai, 1, update_tx[idx])
          update_tx.splice(idx, 1)
          if (!update_tx.length) delete transactions[j]['update']
          break;
        }
      }
    })
  }

  return transactions;
}

const createColsHash = colDefs => {
  if (!colDefs) {
    return null;
  }
  var parts = [];
  colDefs.forEach(function (colDef) {
    if (colDef.children) {
      parts.push(colDef.groupId);
      parts.push('[' + createColsHash(colDef.children) + ']');
    } else {
      parts.push(colDef.colId);
      // headerName can change if the aggFunc was changed in a value col. if we didn't
      // do this, then the grid would not pick up on new header names as we move from
      // eg min to max.
      if (colDef.headerName) {
        parts.push(colDef.headerName);
      }
    }
  });
  return parts.join(',');
}


// Convert arrays stored in JSON as strings to arrays.
// The result from backend
export const JSONtoArr = (responseArray) => {
  return responseArray.map(obj => {
      const newObj = { ...obj }; // Create a new object to avoid modifying the original
      for (const key in newObj) {
          // String that starts with open-bracket expected to be a JSON parseable array
          if (newObj.hasOwnProperty(key) && typeof newObj[key] === 'string' && newObj[key][0] == '[') {
              try {
                  newObj[key] = JSON.parse(newObj[key]);
              } catch (error) {
                  // JSON parsing failed, keep the original string value
              }
          }
      }
      return newObj;
  });
};

export default function createServerSideDatasource(id, context, gridRef, setEditMode, editMode, toggleStatusBarComp, displayTotals, chartParams, totalsConfiguration, unsavedTransactionsRef, setDialogRow, hasDefaultAggregate) {
  const colDefHash = useRef('');
  const transactionQueue = useRef({data: {}, updates: {}})
  const [createRow, getRowId] = useRowId();
  const cellUpdatesTimeout = useRef(null)
  const unsavedTransactions = unsavedTransactionsRef || useRef([]); 

  // we only set the pivot result cols if they have changed since the last time. otherwise
  // the cols would reset every time data comes back from the server (which means col
  // width, positioning etc would be lost every time we eg expand a group, or load another
  // block by scrolling down).
  const setPivotResultColsIntoGrid = (pivotResultColDefs, api)=> {
    var newColDefHash = createColsHash(pivotResultColDefs);
    if (colDefHash.current !== newColDefHash) {
      api.setPivotResultColumns(pivotResultColDefs);
      colDefHash.current = newColDefHash;
    }
  }

  const clearTotals = () => {
    if ((displayTotals || hasDefaultAggregate) && gridRef.current.api.getPinnedBottomRowCount() !== 0) {
      gridRef.current.api.setGridOption('pinnedBottomRowData', []);
    }
  }

  const renderTotalIcons = (totals, config) => {
    const container = document.querySelector(config.containerSelector);
    if (!container) return;

    while (container.firstChild) {
      container.removeChild(container.lastChild);
    }
  
    config.columns?.forEach((column, index) => {
      if (column.column_keys.length === 0) {
        return;
      }
      const totalAmount = column.column_keys.reduce((sum, id) => sum + (totals[id] || 0), 0);
      const content = `<h3 className='font-x-large' style='margin: 0; font-size: 2.5rem;'>${formatNumber(totalAmount)}</h3>`;
  
      const component = (
        <VerticalCardWithIcon
          key={column.key}
          header={I18n.t(column.key)}
          content={content}
          color={column.color || config.defaults[index % config.defaults?.length]?.color}
          iconClassName={`fa fa-${column.icon || config.defaults[index % config.defaults?.length]?.icon}`}
        />
      );
  
      const wrapper = document.createElement('div');
      const root = createRoot(wrapper)
      root.render(component);
      container.appendChild(wrapper);
    });
  };

  const onSave = useCallback((mode) => {
    let path = datagrid_update_rows_path({id: context.id, project_id: context.project_id, context_name: context.context_name, _options: true})

    if (mode === 'single_add') {
      path = context?.singleRowAdd?.path ||
        datagrid_add_row_path({id: context.id, project_id: context.project_id, context_name: context.context_name, _options: true});
    } else if (mode === 'single_update') {
      path = datagrid_update_row_path({id: context.id, project_id: context.project_id, context_name: context.context_name, _options: true});
    }

    gridRef.current.api.setGridOption("loading", true);
    //This contains colDefs with updated creatableOptions in that need to be updated
    const updatedSelectionArr = []
    // gridRef.current.columnApi.columnModel.columnDefs
    //                               .filter(column=>column.cellEditorParams?.creatableOptions !== undefined)
    //                               .map(creatableColumn => ({
    //                                 field: creatableColumn.field,
    //                                 selectionArray: creatableColumn.cellEditorParams?.creatableOptions
    //                               }))

    clearTotals();

    if (updatedSelectionArr.length > 0){
      fetchJson(
        datagrid_update_selection_array_path({id: context.id, context_name: context.context_name, _options: true}),
        {
          method: 'POST',
          body: JSON.stringify({update: updatedSelectionArr})
        }
      )
    }

    return fetchJson(
      path,
      {
        method: 'POST',
        body: JSON.stringify({transactions: reduceDuplicateTransactions(unsavedTransactions.current)}),
        rejectBadRequest: true
      }
    ).then(response => {
        unsavedTransactions.current = [];
        gridRef.current.api.refreshServerSide();
        gridRef.current.api.setGridOption("loading", false);
        clearTotals();
        setEditMode(false);
        toggleStatusBarComp(false);
    }).catch(response => {
      console.warn("Error saving datagrid values");
      gridRef.current.api.setGridOption("loading", false);

      response.json().then(responseJson => {
        console.log(responseJson)
      })
    })
  }, []);

  const onDiscard = useCallback(() => {
    if(unsavedTransactions.current.length) {
      unsavedTransactions.current = [];
      gridRef.current.api.setGridOption("loading", true);
      gridRef.current.api.refreshServerSide();
      gridRef.current.api.setGridOption("loading", false);
    }

    setEditMode(false);
    toggleStatusBarComp(false);
  }, []);

  const serverSideDatasource = {
    // called by the grid when more rows are required
    getRows: params => {
      fetchJson(
        context.query_path, 
        {
          body: JSON.stringify({...params.request, ...params.context.get_rows_params, filterModel: params.request.filterModel ?? {}}), 
          method: 'POST'
        }
      ).then(response => {
        if (response) {
          // supply rows for requested block to grid
          params.success({
            rowData: JSONtoArr(response.rows),
            rowCount: response.rowCount,
          });

          if(response.rows.length == 0) {
            params.api.showNoRowsOverlay()
          } else {
            params.api.hideOverlay()
          }

          // Reapply all pending transactions
          if (editMode && unsavedTransactions.current.length > 0)
            reapplyPendingTransactions(response.rows);

          const isTopLevelQuery = params.request.groupKeys.length === 0;
          if (isTopLevelQuery && chartParams?.chartContainerSelector) {
            params.api.getChartModels().forEach(chart => params.api.getChartRef(chart.chartId).destroyChart());
            params.api.createRangeChart({
              ...chartParams,
              chartContainer: document.querySelector(chartParams.chartContainerSelector)
            });
          }

          // For grouped datagrid collections, the top level query will give totals of all rows
          if (isTopLevelQuery && displayTotals && response.totals) {
            params.api.setGridOption('pinnedBottomRowData', response.totals);
            $('.ag-floating-bottom .ag-column-first .ag-group-value').html(I18n.t('total'));
            $('.ag-floating-bottom .ag-row-pinned').addClass('ag-totals-row-color');
            if (totalsConfiguration) {
              renderTotalIcons(response.totals[0], totalsConfiguration);
            }
          }

          if (isTopLevelQuery && hasDefaultAggregate && response.totals) {
            params.api.setGridOption('pinnedBottomRowData', response.totals);
          }
          
          if (params.request.pivotMode && response?.additionalColumns?.length > 0 ) 
            setPivotResultColsIntoGrid(response.additionalColumns, params.api);
        } else {
          // inform grid request failed
          params.fail();
          params.api.hideOverlay()
        }
      });
    }
  };

  /**
   * This function maps each key in the `keys` array to the corresponding value in the `rowData` object, producing an array of values that represents a hierarchical route
   *
   * Example:
   * If rowData = { country: 'Finland', city: 'Tampere' }
   * and keys = ['country', 'city'],
   * the function returns ['Finland', 'Tampere']
   */
  const getRouteFromGroupColumnKeys = (rowData, keys) => {
    return keys.map(key => rowData[key]);
  };

  /**
   * This function checks if the transaction object has an 'add' property, an 'update' property, or neither. It returns a string indicating the transaction type: 'add', 'update', or 'remove'
   */
  const getTransactionType = (transaction) => {
    return transaction.hasOwnProperty('add') ? 'add' : transaction.hasOwnProperty('update')  ? 'update' : 'remove';
  };

  /**
   * This function accesses the grid's column model to obtain the IDs of columns that are currently used for grouping
   */
  const getGroupColumnKeys = () => {
    return gridRef.current.api.getRowGroupColumns().map((g) => g.colId);
  };

  /**
   * This function iterates over unsaved transactions, determines their type, and re-applies each transaction to the grid
   */
  const reapplyPendingTransactions = useCallback((rowData) => {
    if (!gridRef.current) return;

    const groupColumnKeys = getGroupColumnKeys();

    unsavedTransactions.current.forEach(transaction => {
      let transactionType = getTransactionType(transaction);
      let data = transaction[transactionType][0];
      let route = getRouteFromGroupColumnKeys(data, groupColumnKeys);

      if (rowData.find(row => row.id === data.id))
        gridRef.current.api.applyServerSideTransaction({
          route: route,
          [transactionType]: transaction[transactionType]
        });
    })
  }, []);

  /**
   * This function collects data from each parent group up to the root, including the group field and key for each parent, and returns an array of these parent group data objects
   */
  const getAllParentGroupsData = (node) => {
    // Base case: if there is no parent, return an empty array
    if (!node.parent) return [];

    // Recursive case: collect the parent
    const parentData = getAllParentGroupsData(node.parent);

    var {field, data, key} = node.parent;
    if (field) parentData.push({[field]: data[field], groupKey: key});
    return parentData;
  };

  const getAllParentGroupsDataMap = (selectedNode) => {
    const { data, groupKeys } = getAllParentGroupsData(selectedNode).reduce((acc, item) => {
      var { ['groupKey']: deletedValue, ...rest } = item;
      acc.data.push(rest);

      if (deletedValue !== undefined) acc.groupKeys.push(deletedValue);
      return acc;
    }, { data: [], groupKeys: [] });

    return { data, groupKeys }
  };

  const addRow = useCallback(() => {

    const transaction = {
      addIndex: 0,
      add: [createRow()],
    };
    gridRef.current.api.applyServerSideTransaction(transaction);
    unsavedTransactions.current.push(transaction);
  }, []);

  const removeSelectedRows = useCallback(() => {
    const selectedRows =  getServerSideSelectedNodes(gridRef.current);
    if (selectedRows.length === 0) {
      console.warn('[Example] No row selected.');
      return;
    }

    if (!confirm(I18n.t('confirm_row_delete'))){
      return;
    }

    selectedRows.forEach(selectedNode => {
      const { groupKeys } = getAllParentGroupsDataMap(selectedNode);
      const transaction = { route: groupKeys, remove: [selectedNode.data] };

      unsavedTransactions.current.push(transaction);
      gridRef.current.api.applyServerSideTransaction(transaction);
    });
  }, []);

  const onCellEdited = useCallback((event) => {
    const oldData = event.data;
    const field = event.colDef.field;
    // Update transactionQueue with accumulated changes for each row
    transactionQueue.current.data[oldData.id] = oldData;
    transactionQueue.current.updates[oldData.id] = {...(transactionQueue.current?.updates[oldData.id]|| {}), [field]: event.newValue, changed: true};
    if(cellUpdatesTimeout.current){
      clearTimeout(cellUpdatesTimeout.current)
    }
    cellUpdatesTimeout.current = setTimeout(() => processTransactionQueue(event), 1);
  }, []);
  
  const processTransactionQueue = useCallback((rowNode) => {
    cellUpdatesTimeout.current = null;
    if (!Object.keys(transactionQueue.current?.updates)?.length) return;

    forceUpdateRowData(rowNode);

    // Accumulate changes from transactionQueue into a single transaction
    const updatedQueue = Object.keys(transactionQueue.current.updates).map(rowId => (
      {...transactionQueue.current.data[rowId], ...transactionQueue.current.updates[rowId]}
    ))
    const transaction = {
      update: updatedQueue,
    };
  
    // Add the transaction to the unsavedTransactions array
    unsavedTransactions.current.push(transaction);
    gridRef.current.api.applyServerSideTransaction(transaction);
    
    transactionQueue.current = ({data: {}, updates: {}});
  }, []);

  const forceUpdateRowData = useCallback((rowNode) => {
    // None grouping node auto update value, therefore, this would be duplicate, and it'll create infinite loop since setData/setDataValue call onCellEdited
    // parent.level === -1 when no grouping
    // for grouping node, check if the value is already updated or not using lodash deep equal comparison
    if (rowNode && rowNode.node.parent.level !== -1 && !_.isEqual(rowNode.data[rowNode.colDef.field], rowNode.newValue)) {
      var updatedData = {...rowNode.data, [rowNode.colDef.field]: rowNode.newValue};

      // Update the data of the row node
      rowNode.node.setData(updatedData);

      // Refresh the row node to reflect the changes
      rowNode.node.setDataValue(rowNode.colDef.field, rowNode.newValue); // Update specific cell if needed

      // Optionally, refresh the entire row
      // rowNode.node.refreshCells({rowNodes: [rowNode], force: true});
    }
  }, []);

  const autoGroupColumnDef = useMemo(() => {
    return {
      width: 180,
      cellRendererParams: {
        suppressCount: true, // Render manually, see BlankGroupValueRendered
        innerRenderer: AutoGroupValueRenderer
      }
    };
  }, []);

  // When grouped by col, the row update need to be added into unsavedTransactions to update for backend saving
  const addGroupedRowCallback = (params, route, rowData) => {
    const baseTransaction = {
      add: [{...createRow(), ...rowData}],
    }
    params.api.applyServerSideTransaction({...baseTransaction, route});
    unsavedTransactions.current.push(baseTransaction);
  }

  const saveRowDialog = (mode) => {
    onSave(mode);
    setDialogRow(null);
  }

  return {
    onSave, 
    onDiscard, 
    serverSideDatasource,
    addRow,
    removeSelectedRows,
    onCellEdited,
    getRowId,
    autoGroupColumnDef,
    getAllParentGroupsDataMap,
    addGroupedRowCallback,
    saveRowDialog
  };
}
